import React__default from 'react';
import styled from 'styled-components';
import sx from '../sx.js';
import Text from '../components/Text/Text.js';
import AtlasButton from '../components/AtlasButton/AtlasButton.js';
import { Card } from '../components/Card/Card.js';

/**
 * Every Getting Started page Administration Card provides links that give users actions to take.
 *
 * Those actions can be internal, external, or addon actions.
 */

/**
 * The type of analytics metrics your application collects.
 */

const StyledCard = styled(Card).withConfig({
  displayName: "AdminFeaturesCard__StyledCard",
  componentId: "sc-mod5ct-0"
})(["list-style-type:none;", ""], sx);
/**
 * The AdminFeaturesCard component provides information to users about a particular set of administration products and features, and provides links that give users actions to take.
 *
 * Each AdminFeaturesCard component accepts a title, caption, an `actionHandler` function, and an `action`.
 * 
 * Those actions can be internal, external, or addon actions.
 *
 * The action passed to a AdminFeaturesCard button's onClick handler is of type `AdminFeatureCardAction`. Every
 * `AdminFeatureCardAction` should have a type, a url, a label, and a tracking object, which itself consists of
 * a destination string and a type.
 *
 * Available ActionTypes: "EXTERNAL" | "INTERNAL" | "ADDON";
 *
 * Available DataTrackingTypes: "button" | "link" | "video";
 * 
 * For example, let's say you had an action that took this shape:
 * 
 * ```javascript
 const productConfiguration: {
    type: 'ADDON',
    url: "<your-url>,
    label: "Product configuration",
    tracking: {
      destination: "my-product:configuration-editor",
      type: 'link',
    },
  },
 * ```

You could build an object that held `AdminFeaturesCard` component data like this:

```jsx
export const adminFeaturesData = {
  cardOne: {
    title: "Global Configuration",
    action: {
      ...productConfiguration,
      label: "Go to configuration",
    },
    caption:
      "Configure notifications, auto-refresh issues, showing transitions activity on the issue view, and more.",
  },
  cardTwo: {// more card data here },
  cardTwo: {// more card data here }
}
```

That action could then be destructured and provided to the FooterCard and rendered in the links section, like this:

```jsx

const {cardOne} = adminFeaturesData

function YourComponent() {
  return (
    <AdminFeaturesCard
      title={cardOne.title}
      caption={cardOne.caption}
      action={cardOne.action}
      actionHandler={actionHandler}
    />
  )
}


```
 */
const AdminFeaturesCard = ({
  title,
  caption,
  action,
  actionHandler
}) => {
  console.warn('AdminFeaturesCard is deprecated. Use the v1.1 layout for Getting Started screens instead. See: https://www.fuegokit.design/react-storybook/?path=/story/patterns-getting-started-page--getting-started-page-growth');
  return /*#__PURE__*/React__default.createElement(StyledCard, {
    as: "li",
    sx: {
      borderRadius: '4px',
      border: '1px solid',
      borderColor: 'border.default',
      backgroundColor: 'elevation.surface.default.default',
      width: 'clamp(308px, 50%, 356px)',
      padding: 3,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    "data-testid": "card-component"
  }, /*#__PURE__*/React__default.createElement(Card.Content, null, /*#__PURE__*/React__default.createElement(Card.Header, null, /*#__PURE__*/React__default.createElement(Text, {
    as: "h2",
    sx: {
      fontSize: 2,
      fontWeight: 'fontWeights.3',
      color: 'text.default'
    }
  }, title)), /*#__PURE__*/React__default.createElement(Card.Body, {
    sx: {
      padding: '12px 0px'
    }
  }, /*#__PURE__*/React__default.createElement(Text, {
    as: "p",
    sx: {
      color: 'text.subtlest',
      fontWeight: 'normal',
      lineHeight: 'default',
      fontSize: 2
    }
  }, caption)), /*#__PURE__*/React__default.createElement(Card.Footer, {
    sx: {
      marginTop: 'auto',
      padding: '12px 0 6px 0',
      fontSize: '2'
    }
  }, /*#__PURE__*/React__default.createElement(AtlasButton, {
    as: "button"
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ,
    onClick: () => actionHandler ? actionHandler(action) : null,
    "data-testid": "admin-card-btn"
  }, action === null || action === void 0 ? void 0 : action.label))));
};
AdminFeaturesCard.displayName = "AdminFeaturesCard";
var AdminFeaturesCard$1 = AdminFeaturesCard;

export { AdminFeaturesCard$1 as default };
